import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import io from "socket.io-client";
import api from "../APIs/axios";
import ChatSession from "../chat/ChatSession";

const ChatContext = createContext();

const ENDPOINT = process.env.REACT_APP_BASE_URL;

const ChatProvider = ({ children }) => {
  const [selectedChat, setSelectedChat] = useState();
  const [user, setUser] = useState();
  const [notification, setNotification] = useState([]);
  const [chats, setChats] = useState();
  const socketRef = useRef(null);
  const [chatSession, setChatSession] = useState(null);

  const history = useHistory();
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem("accessToken"));
  useEffect(() => {
    const changed = () => setSessionToken(localStorage.getItem("accessToken"));
    const unlisten = history.listen(changed);
    window.addEventListener("storage", changed);
    return () => { unlisten(); window.removeEventListener("storage", changed); };
  }, [history]);

  useEffect(() => {
    setChatSession(null); setSelectedChat(undefined); setChats(undefined); setNotification([]);
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    const resolvedUser = { token: userInfo?.token, ...userInfo?.data, id: 0 };
    setUser(resolvedUser);

    if (ENDPOINT && !socketRef.current) {
      socketRef.current = io(ENDPOINT, { transports: ["websocket"] });
      const socket = socketRef.current;
      // Rooms are lost on reconnect; re-establish the existing setup contract.
      socket.on("connect", () => socket.emit("setup", resolvedUser));
    }

    let stopped = false;
    let session;
    const token = sessionToken;
    const onVisibility = () => { if (!document.hidden && session) session.resume(); };
    if (token && ENDPOINT) api("chat-v2/capabilities", "GET", {}, { timeout: 10000 }).then(({ data }) => {
      if (stopped || !data.enabled || token !== localStorage.getItem("accessToken")) return;
      const realtimeSocket = io(`${ENDPOINT.replace(/\/$/, "")}/chat-v2`, { transports: ["websocket"], auth: { token }, autoConnect: false });
      session = new ChatSession({
        request: (path, method, body) => api(path, method, body, { timeout: 30000, headers: { "x-access-token": token } }),
        socket: realtimeSocket, actorKey: data.actor_key, storage: localStorage, storageScope: ENDPOINT,
        hidden: () => document.hidden,
        onInbox: () => window.dispatchEvent(new Event("chat-v2-inbox")),
      });
      if (socketRef.current) socketRef.current.disconnect(); socketRef.current = null;
      setChatSession(session); realtimeSocket.connect();
      document.addEventListener("visibilitychange", onVisibility);
    }).catch((error) => {
      // Keep the compatible path usable while making a failed negotiation
      // diagnosable without logging tokens, request bodies or signed URLs.
      console.warn('[chat-v2] negotiation unavailable', {
        status: error.response?.status || null,
        code: error.code || error.name || 'Error',
      });
    });

    return () => {
      stopped = true; if (session) {
        if (token !== localStorage.getItem("accessToken")) session.clearDrafts();
        session.close();
      }
      document.removeEventListener("visibilitychange", onVisibility);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  return (
    <ChatContext.Provider
      value={{
        selectedChat,
        setSelectedChat,
        user,
        setUser,
        notification,
        setNotification,
        chats,
        setChats,
        socket: socketRef.current,
        socketRef,
        chatSession,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const ChatState = () => useContext(ChatContext);

export default ChatProvider;
